// A hero's order, and the button that gives them one.
//
// The order itself is the sim's (`heroes.quests`); the panel shows what the
// sim says and asks for a new one. A hero with an order may be given
// another — the new one replaces the old.

import { useState } from 'react';
import type { HeroQuest, HeroQuestKind, HuntTarget } from '../../heroes/quests.js';
import { useT } from '../../i18n/index.js';
import { appDom } from '../../lib/appDom.js';
import { HeroQuestDialog } from '../Modal/HeroQuestDialog.js';

export interface HeroQuestPanelProps {
  /** The hero's entity id — the same number the quest carries. */
  readonly heroId: number;
  readonly heroName: string;
  /** The order the hero carries now, if any. */
  readonly quest: HeroQuest | null;
  /** The enemy heroes the client can see. */
  readonly huntTargets: readonly HuntTarget[];
  /** Give the hero an order. */
  readonly onDispatchHeroQuest?: (
    unitId: string,
    kind: HeroQuestKind,
    amount?: number,
    targetUnitId?: string,
  ) => boolean;
}

export function HeroQuestPanel({
  heroId,
  heroName,
  quest,
  huntTargets,
  onDispatchHeroQuest,
}: HeroQuestPanelProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const unitId = `unit_${heroId}`;

  return (
    <div className="flex flex-col gap-1.5" data-testid="hero-quest-panel">
      {quest && (
        <div className="rounded bg-amber-500/10 px-2 py-1 text-[10px] text-amber-700 dark:text-amber-300">
          <div className="flex items-center gap-1.5">
            <span className="font-semibold">{t('heroes.order')}</span>
            <span>{describe(t, quest, huntTargets)}</span>
            {quest.state === 'active' && (
              <span className="text-muted-foreground">
                {t('heroes.quest.progress', { made: quest.progress, needed: quest.amount })}
              </span>
            )}
          </div>
          <div className="text-muted-foreground">
            {quest.state === 'active'
              ? t('heroes.quest.due', { n: quest.deadline })
              : t(`heroes.quest.${quest.state}`)}
          </div>
        </div>
      )}

      {onDispatchHeroQuest && (
        <button
          type="button"
          data-testid="hero-quest-open"
          {...appDom({
            id: 'hero-quest-open',
            type: 'Button',
            role: 'button',
            scope: 'section',
          })}
          onClick={() => setOpen(true)}
          className="self-start rounded border border-border/60 px-2 py-0.5 text-[10px] hover:bg-muted/60"
        >
          {t('heroes.giveOrder')}
        </button>
      )}

      {open && (
        <HeroQuestDialog
          heroName={heroName}
          huntTargets={huntTargets}
          onOrder={(kind, amount, targetUnitId) => {
            onDispatchHeroQuest?.(unitId, kind, amount, targetUnitId);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

/** The order in one line: what the hero is for. */
function describe(
  t: ReturnType<typeof useT>,
  quest: HeroQuest,
  huntTargets: readonly HuntTarget[],
): string {
  if (quest.kind === 'hunt') {
    const named = huntTargets.find((x) => x.unitId === `unit_${quest.target}`);
    return named ? t('heroes.order.hunt', { name: named.name }) : t('heroes.order.kind.hunt');
  }
  return t('heroes.order.slay', { n: quest.amount });
}
